import React from 'react';
import { IoEye, IoEyeOff } from 'react-icons/io5';
import { FieldTypes, shouldShowField, getFieldValue } from '@/config/integrationConfigs';

/**
 * IntegrationFieldRenderer - Renders form fields dynamically based on integration configuration
 * 
 * This component simplifies the AppSecrets form by showing only relevant fields
 * for the selected integration, with integration-specific labels and validation.
 * 
 * @param {Object} props
 * @param {Array} props.fields - Array of field configurations from integration config
 * @param {Object} props.secret - Current secret values
 * @param {Function} props.onFieldChange - Callback for field value changes
 * @param {Object} props.visiblePasswords - State tracking which password fields are visible
 * @param {Function} props.onTogglePasswordVisibility - Callback to toggle password visibility
 * @param {Object} props.availableSecrets - Available secrets for dynamic select options
 * @param {Function} props.isValidUrl - URL validation function
 */
const IntegrationFieldRenderer = ({
    fields,
    secret,
    onFieldChange,
    visiblePasswords,
    onTogglePasswordVisibility,
    availableSecrets = {},
    isValidUrl
}) => {
    /**
     * Get dynamic options for select fields (e.g., Azure AD secrets)
     */
    const getDynamicOptions = (optionsKey) => {
        if (optionsKey === 'azureAdSecrets') {
            return Object.entries(availableSecrets)
                .flatMap(([category, secrets]) =>
                    secrets
                        .filter(s => s.secretType === 'AzureAd' && s.id !== secret?.id)
                        .map(s => ({
                            value: s.secretKey,
                            label: `${s.displayName || s.secretKey} (${category})`
                        }))
                );
        }
        return [];
    };

    /**
     * Render a single field based on its type and configuration
     */
    const renderField = (field) => {
        // Check if field should be shown based on conditional logic
        if (!shouldShowField(field, secret)) {
            return null;
        }

        const value = getFieldValue(secret, field.key);
        const fieldId = `field-${field.key}`;

        // Render different field types
        switch (field.type) {
            case FieldTypes.PASSWORD:
            case FieldTypes.TEXTAREA:
                return (
                    <div key={field.key} className={`form-field password-field ${field.fullWidth ? 'full-width' : ''}`}>
                        <label htmlFor={fieldId}>
                            {field.label}
                            {field.required && ' *'}
                        </label>
                        <div className="password-input-group">
                            {field.type === FieldTypes.TEXTAREA ? (
                                <textarea
                                    id={fieldId}
                                    value={value}
                                    onChange={e => onFieldChange(field.key, e.target.value)}
                                    placeholder={field.placeholder}
                                    rows={3}
                                    required={field.required}
                                    style={{ fontFamily: visiblePasswords[field.key] ? 'inherit' : 'monospace' }}
                                />
                            ) : (
                                <input
                                    id={fieldId}
                                    type={visiblePasswords[field.key] ? 'text' : 'password'}
                                    value={value}
                                    onChange={e => onFieldChange(field.key, e.target.value)}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                />
                            )}
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => onTogglePasswordVisibility(field.key)}
                                aria-label="Toggle password visibility"
                            >
                                {visiblePasswords[field.key] ? <IoEyeOff /> : <IoEye />}
                            </button>
                        </div>
                        {field.help && <small className="field-help">{field.help}</small>}
                    </div>
                );

            case FieldTypes.CHECKBOX:
                return (
                    <div key={field.key} className="form-field checkbox-field">
                        <label className="checkbox-label">
                            <input
                                id={fieldId}
                                type="checkbox"
                                checked={value === true}
                                onChange={e => onFieldChange(field.key, e.target.checked)}
                            />
                            {field.label}
                        </label>
                        {field.help && <small className="field-help">{field.help}</small>}
                    </div>
                );

            case FieldTypes.SELECT: {
                const options = field.dynamicOptions 
                    ? getDynamicOptions(field.dynamicOptions)
                    : field.options || [];

                return (
                    <div key={field.key} className={`form-field ${field.fullWidth ? 'full-width' : ''}`}>
                        <label htmlFor={fieldId}>
                            {field.label}
                            {field.required && ' *'}
                        </label>
                        <select
                            id={fieldId}
                            value={value}
                            onChange={e => onFieldChange(field.key, e.target.value)}
                            required={field.required}
                        >
                            <option value="">-- Select {field.label} --</option>
                            {options.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {field.help && <small className="field-help">{field.help}</small>}
                    </div>
                );
            }

            case FieldTypes.NUMBER:
                return (
                    <div key={field.key} className={`form-field ${field.fullWidth ? 'full-width' : ''}`}>
                        <label htmlFor={fieldId}>
                            {field.label}
                            {field.required && ' *'}
                        </label>
                        <input
                            id={fieldId}
                            type="number"
                            value={value ?? ''}
                            onChange={e => {
                                const numValue = parseInt(e.target.value, 10);
                                onFieldChange(field.key, isNaN(numValue) ? null : numValue);
                            }}
                            placeholder={field.placeholder}
                            required={field.required}
                        />
                        {field.help && <small className="field-help">{field.help}</small>}
                    </div>
                );

            case FieldTypes.URL:
            case FieldTypes.TEXT:
            default: {
                const isUrl = field.type === FieldTypes.URL;
                const hasValidationError = isUrl && value && isValidUrl && !isValidUrl(value);

                return (
                    <div key={field.key} className={`form-field ${field.fullWidth ? 'full-width' : ''}`}>
                        <label htmlFor={fieldId}>
                            {field.label}
                            {field.required && ' *'}
                        </label>
                        <input
                            id={fieldId}
                            type="text"
                            value={value}
                            onChange={e => onFieldChange(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            style={{
                                borderColor: hasValidationError ? 'red' : undefined
                            }}
                        />
                        {field.help && <small className="field-help">{field.help}</small>}
                        {hasValidationError && (
                            <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
                                Invalid URL format. Must start with http:// or https://
                            </small>
                        )}
                    </div>
                );
            }
        }
    };

    return (
        <div className="integration-fields">
            {fields.map(field => renderField(field))}
        </div>
    );
};

export default IntegrationFieldRenderer;
